import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { toast } from "react-toastify";
import { Config } from "src/constants";
import { StringService, _ } from "src/services";
import { Modals, ModalsAction, UserSession, UserSessionAction } from "src/store";
import { Loading } from "src/components/ui";
import { Modal } from "src/components/modals/modal";


export const LoginModal: React.FC = () => {

    //#region dependencies

    const dispatch = useDispatch();
    const { location, ...history } = useHistory();
    const modals: Modals = useSelector((state: any) => state.ModalsState);
    const user: UserSession = useSelector((state: any) => state.UserState);

    //#end region

    //#region properties

    const telRef = useRef<HTMLInputElement>(null);
    const codeRef = useRef<HTMLInputElement>(null);
    const timer = useRef<NodeJS.Timeout | null>(null);

    //#end region

    //#region states

    const [state, setState] = useState({
        phoneNumber: "",
        verificationCode: "",
        step: 1,
        countdown: 0,
    });

    //#end region

    //#region methods

    const clearSession = () => {
        if (!user.loggedIn) dispatch(UserSessionAction.clearLoginInfos());

        setState({
            phoneNumber: "",
            verificationCode: "",
            step: 1,
            countdown: 0,
        });
    };

    const clearIntervals = () => {
        try {
            if (timer.current) clearInterval(timer.current);
        }
        catch { }
    };

    const returnToPreviousRoute = () => {
        //@ts-ignore
        if (location.state && location.state.from) {
            if (user.loggedIn)
                //@ts-ignore
                history.push(location.state.from);
            else
                history.push(location.pathname);
        }
    };

    const onClose = () => {
        dispatch(ModalsAction.hideLogin());
        clearSession();
        clearIntervals();
        returnToPreviousRoute();
    };

    const onShow = () => {
        clearSession();
        if (telRef && telRef.current) {
            telRef.current.focus();
        }
    };

    const onTelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setState((state) => ({ ...state, phoneNumber: value }));
    };

    const onVerificationCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setState((state) => ({ ...state, verificationCode: value }));
    };

    const handlePhoneKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (state.step === 1 || state.countdown === 0) {
            if (e.key === "Enter" || e.keyCode === 13) sendCode();
        }
        if (state.step === 2) {
            if (e.key === "Enter" || e.keyCode === 13) {
                state.countdown > 0 ? verifyCode() : sendCode();
            }
        }
    };

    const sendCode = (e?: React.MouseEvent<HTMLButtonElement>) => {

        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        if (!state.phoneNumber || !state.phoneNumber.trim()) {
            toast.error(_("cellRequiredMessage"));
            return;
        }
        setTimeout(() => {
            if (user.loading) return;
            dispatch(UserSessionAction.requestLoginCode(state.phoneNumber));
        });
    };

    const verifyCode = (e?: React.MouseEvent<HTMLButtonElement>) => {

        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        if (!state.verificationCode || !state.verificationCode.trim() || state.verificationCode.length < Config.lengthCode) {
            toast.error(_("verificationError"));
            return;
        }

        setTimeout(() => {
            if (!user.user || user.loading) return;
            dispatch(UserSessionAction.verifyLoginCode(user.user.id, user.user.msisdn, state.verificationCode));
        });
    };

    //#end region

    //#region effects

    useEffect(() => {
        if (modals.login && user.loggedIn) {
            onClose();
            return;
        }

        if (user.user && modals.login && !state.countdown) {

            if (!user.user.accessToken) {
                setState((state) => ({ ...state, step: 2, countdown: Config.resendVerificationCodeTimeout }));

                clearIntervals();

                timer.current = setInterval(() => {

                    setState((state) => {
                        if (state.countdown <= 1) clearIntervals();
                        return ({ ...state, countdown: state.countdown > 0 ? state.countdown - 1 : 0 });
                    });

                }, 1000);
            }

        }

        if (user.user && user.loggedIn && user.user.profilePromotion && !user.user.updateNotify) {
            dispatch(ModalsAction.showCompleteProfile());
        }

        if (!user.user) clearSession();

        return (() => {
            clearIntervals();
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user.user, modals.login]);

    //#end region

    //#region helpers

    const renderFooter = () => {
        if (state.step === 1) {
            return (
                <div className="col-xs-12 col-sm-4">
                    <button className="float-left btn btn-submit w-100 " tabIndex={0} onClick={sendCode}>
                        {!user.loading && <span>{_("sendOTP")}</span>}
                        {user.loading && <Loading width={30} height={30} />}
                    </button>
                </div>
            );
        }

        if (state.countdown > 0) {
            return (
                <>
                    <div className="col-xs-12 col-sm-5 order-1 order-sm-0">
                        <button tabIndex={0} className="w-100 btn btn-md btn-submit text-rtl" onClick={verifyCode}
                            disabled={!state.verificationCode}>
                            {!user.loading && <span>{_("verifyOTP")}</span>}
                            {user.loading && <Loading width={30} height={30} />}
                        </button>
                    </div>
                    <div className="col-xs-12 col-sm-7 text-sm-right text-center py-2 order-0 order-sm-1 pb-4 pb-sm-2">
                        <span className="text-timer">
                            {_("resendOTPAfter")} {state.countdown}
                        </span>
                    </div>
                </>
            );
        }

        return (
            <div className="col-xs-12 col-sm-5 order-1 order-sm-0">
                <button tabIndex={0} className="w-100 btn btn-md btn-submit text-rtl" onClick={sendCode}>
                    {!user.loading && <span>{_("resendOTP")}</span>}
                    {user.loading && <Loading width={30} height={30} />}
                </button>
            </div>
        );

    };

    const renderBody = () => {
        if (state.step === 1) {
            return (
                <>
                    <p className="rtl">{_("loginDescription")}</p>
                    <p className="rtl">{_("enterYourCellMessage")}</p>
                    <div className="form-group">
                        <div className="row justify-content-center">
                            <div className="col-xs-12 col-md-6">
                                <input
                                    onKeyPress={handlePhoneKeyPress}
                                    value={state.phoneNumber}
                                    onChange={onTelChange}
                                    ref={telRef}
                                    placeholder="۰۹*********"
                                    autoFocus
                                    className="text-center form-control"
                                    type="tel"
                                    pattern="\d*"
                                    maxLength={11}
                                />
                            </div>
                        </div>
                    </div>
                </>
            );
        }
        const message = _("enterOTP").split("{cell}");
        return (
            <>
                <p className="rtl">{message[0] || ""} <span className="display-inline-block ltr">{StringService.toPhone(state.phoneNumber)}</span> {message[1] || ""}</p>
                <div className="form-group">
                    <div className="row justify-content-center">
                        <div className="col-xs-12 col-md-6">
                            <input
                                className="text-center form-control"
                                onKeyPress={handlePhoneKeyPress}
                                value={state.verificationCode}
                                onChange={onVerificationCodeChange}
                                ref={codeRef}
                                autoFocus
                                type="tel"
                                maxLength={5}
                            />
                        </div>
                    </div>
                </div>
            </>
        );
    };

    const renderTitle = () => {
        if (state.step === 1) return _("loginTitle");
        if (state.step === 2) return "وارد کردن کد تایید"
    }

    //#end region

    return (
        <Modal title={renderTitle()} show={modals.login} onClose={onClose} onShow={onShow} renderFooter={renderFooter} className="modal-component--login">
            <div className="content text-center">
                {renderBody()}
            </div>
        </Modal>
    );

};